import { Box, CloseButton, Slide, useBreakpointValue } from "@chakra-ui/react";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useIntercom } from "react-use-intercom";
import { urls } from "../../urls";
import { AssistantChat } from "./AssistantChat";
import { useAssistantPanel } from "./assistantPanelContext";

const PANEL_WIDTH_PX = 512;
const INTERCOM_DEFAULT_PADDING_PX = 20;

// A non-modal slide-over: no overlay, no focus trap, page stays fully interactive while it's open.
// zIndex sits at Chakra's "overlay" layer (below "modal") so a real modal — e.g. ConversationSwitcher's
// delete-confirmation dialog, rendered inside this panel — still stacks above it.
export const AssistantPanel = () => {
  const { panelOpen, closePanel } = useAssistantPanel();
  const { pathname } = useLocation();
  const { update } = useIntercom();
  const onAssistantPage = pathname === urls.v2.assistant;

  useEffect(() => {
    if (onAssistantPage) {
      closePanel();
    }
  }, [onAssistantPage, closePanel]);

  // The Intercom launcher floats above the panel and would sit right on top of the chat composer's
  // send button — shift it to the panel's left edge while the panel is open on desktop; on mobile
  // the panel is full-width, so there is nowhere to shift to and the launcher hides instead.
  const isMobile = useBreakpointValue({ base: true, md: false });
  useEffect(() => {
    update({
      hideDefaultLauncher: Boolean(panelOpen && isMobile),
      horizontalPadding:
        panelOpen && !isMobile ? PANEL_WIDTH_PX + INTERCOM_DEFAULT_PADDING_PX : INTERCOM_DEFAULT_PADDING_PX,
    });
  }, [panelOpen, isMobile, update]);

  if (onAssistantPage) {
    return null;
  }

  return (
    <Slide
      direction="right"
      in={panelOpen}
      unmountOnExit
      style={{ zIndex: "var(--chakra-zIndices-overlay)", width: "min(100%, 32rem)" }}
    >
      <Box
        h="full"
        display="flex"
        flexDirection="column"
        overflow="hidden"
        bg="smBackground"
        borderLeftWidth="1px"
        borderColor="smBorder"
        boxShadow="lg"
        position="relative"
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            closePanel();
          }
        }}
      >
        <CloseButton onClick={closePanel} position="absolute" top="2" insetEnd="3" />
        <Box flex="1" display="flex" flexDirection="column" overflow="hidden" pt="10" pb="4" px="6">
          <AssistantChat />
        </Box>
      </Box>
    </Slide>
  );
};
